// Dispatches by hostname so image transforms happen at the CDN edge rather
// than via Next's /_next/image endpoint. Drops the sharp runtime dependency
// entirely and pushes CPU cost to the upstream CDN.

type LoaderArgs = {
  src: string;
  width: number;
  quality?: number;
};

const CONTENTFUL_IMAGE_HOST = 'images.ctfassets.net';
const CONTENTFUL_PASSTHROUGH_EXTENSIONS = new Set(['.gif', '.svg']);

const OPENDOOR_IMAGE_HOST = 'images.opendoor.com';
const IMGDROP_PATH_PREFIX = '/source/s3/imgdrop-production/';

export function normalizeImageSrc(src: string): string {
  return src.startsWith('//') ? `https:${src}` : src;
}

function getPathExtension(pathname: string): string {
  const filename = pathname.split('/').pop() ?? '';
  const extensionIndex = filename.lastIndexOf('.');

  return extensionIndex === -1 ? '' : filename.slice(extensionIndex).toLowerCase();
}

export function isContentfulRasterImageUrl(src: string): boolean {
  try {
    const url = new URL(normalizeImageSrc(src));

    return (
      url.hostname === CONTENTFUL_IMAGE_HOST &&
      !CONTENTFUL_PASSTHROUGH_EXTENSIONS.has(getPathExtension(url.pathname))
    );
  } catch {
    return false;
  }
}

// `images.opendoor.com/source/s3/imgdrop-production/<key>` is the canonical
// host for assets uploaded through imgdrop.opendoor.com (the green path for
// new Beacon assets). The Opendoor imgproxy in front of those S3 sources
// honours `?preset=square-{128|256|512|1024|2048}` to resize + transcode to
// AVIF or WebP at request time via Accept-header content negotiation.
// Free-form `w=`/`fm=`/`q=` params return 403 on imgdrop sources — only the
// named presets work, and unknown preset names (e.g., `square-160`,
// `hero`, `landscape-1200`) silently passthrough to the raw source PNG.
// The `square-*` names are misleading: they preserve the source aspect
// ratio with max-dimension = N (e.g., `square-1024` on a 1200x800 PNG
// returns ~1024x682, not 1024x1024).
export function isImgdropImageUrl(src: string): boolean {
  try {
    const url = new URL(normalizeImageSrc(src));

    return url.hostname === OPENDOOR_IMAGE_HOST && url.pathname.startsWith(IMGDROP_PATH_PREFIX);
  } catch {
    return false;
  }
}

function imgdropPresetForWidth(width: number): string {
  // imgdrop exposes only square-{128,256,512,1024,2048}. Pick the preset
  // nearest in log space (boundaries at the geometric mean of adjacent
  // presets) rather than ceiling-ing up. Ceiling-ing mapped every Next/Image
  // candidate width in (512,1024] to square-1024, so a 640px card slot fetched
  // a 1024px image (~2x bytes), failing Lighthouse uses-responsive-images and
  // inflating LCP. Nearest-in-log-space maps 640 -> square-512 and
  // 1080 -> square-1024, trading a little upscaling for a large byte cut.
  if (width <= 181) return 'square-128'; // sqrt(128 * 256)
  if (width <= 362) return 'square-256'; // sqrt(256 * 512)
  if (width <= 724) return 'square-512'; // sqrt(512 * 1024)
  if (width <= 1448) return 'square-1024'; // sqrt(1024 * 2048)
  return 'square-2048';
}

export default function imageLoader({ src, width, quality }: LoaderArgs): string {
  const q = quality ?? 75;
  const normalizedSrc = normalizeImageSrc(src);

  if (isContentfulRasterImageUrl(normalizedSrc)) {
    // https://www.contentful.com/developers/docs/references/images-api/
    return withQuery(normalizedSrc, { w: String(width), fm: 'avif', q: String(q) });
  }

  if (isImgdropImageUrl(normalizedSrc)) {
    // The imgproxy negotiates AVIF/WebP/JPEG from the browser's Accept
    // header; Next/Image sets that automatically. We only pick the size
    // preset here. `quality` is intentionally ignored — the preset baseline
    // is fixed by the imgproxy and free-form `q=` returns 403.
    return withQuery(normalizedSrc, { preset: imgdropPresetForWidth(width) });
  }

  return normalizedSrc;
}

function withQuery(src: string, params: Record<string, string>): string {
  const url = new URL(src);
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, v);
  }
  return url.toString();
}
